/**
 * lib/image-mapper.ts
 *
 * Maps article slugs to their corresponding generated images.
 * This guarantees proper image resolution on both the grid hub page
 * and individual article detail views.
 */

export const ARTICLE_IMAGE_MAP: Record<string, string> = {
  "roof-lifespan-by-material": "/images/articles/roof-lifespan.webp",
  "10-signs-you-need-a-new-roof": "/images/articles/signs-need-roof.webp",
  "hvac-replacement-cost-guide": "/images/articles/hvac-cost.webp",
  "signs-your-ac-is-dying-repair-vs-replace": "/images/articles/signs-ac-dying.webp",
  "heat-pump-vs-central-ac-comparison": "/images/articles/heat-pump-vs-central.webp",
  "kitchen-remodel-cost-guide": "/images/articles/kitchen-cost.webp",
  "bathroom-remodel-cost-guide": "/images/articles/bathroom-cost.webp",
  "renovations-add-resale-value": "/images/articles/renovations-resale-value.webp",
  "solar-panel-costs": "/images/articles/solar-costs.webp",
  "solar-panel-payback-period-explained": "/images/articles/solar-payback.webp",
  "window-replacement-cost-guide": "/images/articles/window-cost.webp",
  "cost-to-build-a-deck-guide": "/images/articles/deck-cost.webp",
  "how-much-concrete-do-i-need-guide": "/images/articles/concrete-need.webp",
  "how-much-paint-do-you-need-per-room": "/images/articles/paint-need.webp",
  "vinyl-vs-laminate-vs-hardwood-flooring": "/images/articles/flooring-type.webp",
  "flooring-installation-cost-per-square-foot": "/images/articles/flooring-cost.webp",
  "how-long-does-a-water-heater-last": "/images/articles/water-heater-lifespan.webp",
  "best-time-for-renovations": "/images/articles/best-time-renovations.webp",
  "wood-vs-vinyl-vs-metal-fence-comparison": "/images/articles/fence-comparison.webp",
  "why-is-my-electric-bill-so-high-this-summer": "/images/articles/electric-bill-high.webp",
  "will-new-ac-lower-electric-bill": "/images/articles/new-ac-bill.webp",
  "how-much-attic-insulation-do-you-need": "/images/articles/attic-insulation.webp",
  "seer-rating-explained-ac-bill": "/images/articles/seer-rating.webp",
  "smart-thermostat-savings-guide": "/images/articles/smart-thermostat.webp",
  "2026-hurricane-season-home-prep": "/images/articles/hurricane-prep.webp",
  "does-homeowners-insurance-cover-roof-replacement": "/images/articles/roof-insurance.webp",
  "impact-windows-vs-hurricane-shutters-cost": "/images/articles/impact-windows.webp",
  "how-to-photograph-roof-for-insurance-claim": "/images/articles/photograph-roof.webp",
  "wind-rated-garage-doors-guide": "/images/articles/wind-garage-door.webp",
  "pre-listing-home-inspection-checklist": "/images/articles/pre-listing-inspection.webp",
  "cost-to-paint-house-exterior": "/images/articles/exterior-painting.webp",
  "driveway-sealing-cost-guide": "/images/articles/driveway-sealing.webp",
  "in-ground-vs-above-ground-pool-cost": "/images/articles/pool-cost.webp",
  "cost-to-sod-or-reseed-lawn": "/images/articles/lawn-sod-seed.webp",
  "gutter-replacement-cost-guide": "/images/articles/gutter-replacement.webp",
  "siding-replacement-cost-guide": "/images/articles/siding-replacement.webp",
  "is-home-energy-audit-worth-it": "/images/articles/energy-audit.webp",
  "basement-waterproofing-cost-guide": "/images/articles/basement-waterproofing.webp",
  "complete-roofing-guide": "/images/articles/complete-roofing.webp",
  "complete-concrete-guide": "/images/articles/complete-concrete.webp",
  "complete-painting-guide": "/images/articles/complete-painting.webp",
  "complete-flooring-guide": "/images/articles/complete-flooring.webp",
  "complete-hvac-guide": "/images/articles/complete-hvac.webp",
  "complete-kitchen-remodel-guide": "/images/articles/complete-kitchen-remodel.webp",
  "complete-bathroom-guide": "/images/articles/complete-bathroom.webp",
  "florida-garage-door-wind-code-requirements": "/images/articles/florida-wind-garage-door.webp",
};

/**
 * Gets the representative image path for an article slug.
 * Fallback is a generic placeholder or default site image.
 */
export function getArticleImage(slug: string): string {
  return ARTICLE_IMAGE_MAP[slug] || "/images/concrete_material_guide.webp";
}

export const CALCULATOR_IMAGE_MAP: Record<string, { buyingGuide: string; materialGuide: string }> = {
  "basement-finishing": {
    buyingGuide: "/images/basement-finishing_buying_guide.webp",
    materialGuide: "/images/basement-finishing_site_prep.webp",
  },
  insulation: {
    buyingGuide: "/images/insulation_buying_guide.webp",
    materialGuide: "/images/insulation_site_prep.webp",
  },
};

export function getCalculatorImage(slug: string, category: "diy" | "high-value"): string {
  const mapped = CALCULATOR_IMAGE_MAP[slug];
  if (mapped) {
    return category === "diy" ? mapped.materialGuide : mapped.buyingGuide;
  }

  return category === "diy" ? `/images/${slug}_material_guide.webp` : `/images/${slug}_buying_guide.webp`;
}
